import { Link } from "react-router-dom";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import Reveal from "../../components/Reveal";

const METHODOLOGY = [
  ["Literature Review", "Reviewed existing research on sex education access for adolescents with autism, identifying a gap in caregiver-focused studies within a Ghanaian context."],
  ["Survey & Interview Design", "Designed a mixed-methods instrument combining structured survey questions with open-ended interview prompts for caregivers of teenagers with autism."],
  ["Data Collection", "Collected responses from caregivers, capturing both quantitative ratings of knowledge/comfort and qualitative reflections on lived experience."],
  ["Quantitative Analysis", "Analysed survey data in Excel to identify patterns in caregiver knowledge, attitudes, and access to resources."],
  ["Findings Synthesis", "Synthesised quantitative and qualitative findings into a coherent account of what caregivers know, want, and lack support for."],
];

export default function ProjectAutismSexEducation() {
  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pt-32 md:px-12 md:pt-40">
        <Link to="/projects" className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint hover:text-heading">
          &larr; Back to Projects
        </Link>
      </div>

      <Reveal className="mx-auto max-w-content px-6 pb-14 pt-8 md:px-12 md:pb-20">
        <p className="eyebrow">Undergraduate Research &middot; Adolescent Health</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          Autism &amp; <em className="italic text-gold-500">Sex Education</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-[1rem] leading-relaxed text-ink-soft">
          Undergraduate research examining the knowledge and perceptions of caregivers on sex education for
          teenagers with autism. Mixed-methods data collection and quantitative Excel analysis; findings
          presented to faculty.
        </p>
      </Reveal>

      <Reveal as="section" className="border-y border-line bg-surface-alt">
        <div className="mx-auto grid max-w-content grid-cols-3 divide-x divide-line px-6 md:px-12">
          <div className="px-2 py-8 text-center first:pl-0 sm:px-6">
            <div className="font-serif text-3xl text-heading">2023</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Conducted at KNUST</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">Mixed</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Methods Design</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">Faculty</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Findings Presented To</div>
          </div>
        </div>
      </Reveal>

      <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-14 md:grid-cols-[1fr_320px] md:gap-16">
          <div>
            <Reveal className="mb-16">
              <p className="eyebrow">Overview</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">The question behind the research</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Adolescents with autism go through the same physical and social development as any other
                teenager, but the caregivers around them are rarely given adapted guidance on how to talk
                about it. This research set out to understand what caregivers of teenagers with autism
                actually know about sex education, how comfortable they feel discussing it, and where they
                turn to (or don&rsquo;t) for support.
              </p>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                The aim wasn&rsquo;t to judge caregivers for gaps in knowledge, but to identify exactly where
                a targeted, disability-informed resource could make the most difference.
              </p>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Methodology</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">How the study was run</h2>
              <div className="mt-6 space-y-6">
                {METHODOLOGY.map(([title, desc], i) => (
                  <div key={title} className="flex gap-5 border-t border-line pt-5">
                    <span className="font-serif text-lg text-ink-faint">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-serif text-base text-heading">{title}</p>
                      <p className="mt-1 text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <p className="eyebrow">Findings</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">What the data suggests</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Caregivers broadly recognised the importance of sex education for their teenagers but
                reported low confidence delivering it themselves, citing a lack of adapted materials and
                uncertainty about what language and format would actually work for their child. Most
                preferred guidance from a trained professional over self-directed resources, but had limited
                access to either.
              </p>
            </Reveal>
          </div>

          <Reveal as="aside" className="h-fit space-y-6 md:sticky md:top-28">
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Project Info</p>
              <dl className="space-y-3 text-[0.85rem]">
                <div><dt className="text-ink-faint">Type</dt><dd className="text-heading">Undergraduate Research</dd></div>
                <div><dt className="text-ink-faint">Institution</dt><dd className="text-heading">KNUST</dd></div>
                <div><dt className="text-ink-faint">Year</dt><dd className="text-heading">2023</dd></div>
              </dl>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal as="section" className="bg-forest-900">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
          <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Why it matters</p>
          <h2 className="mt-2 font-serif text-2xl font-extrabold text-white md:text-3xl">
            Closing a quiet gap in disability-informed care
          </h2>
          <p className="mt-6 max-w-[62ch] text-[0.95rem] leading-relaxed text-forest-50/80">
            Sex education is a standard part of adolescent health, except when disability enters the
            picture, and the conversation quietly stops. Understanding what caregivers actually need is the
            first step toward materials and programs that don&rsquo;t leave autistic teenagers, or the
            people raising them, without support.
          </p>
        </div>
      </Reveal>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-6 px-6 py-14 md:px-12">
          <p className="font-serif text-xl text-heading">See all projects</p>
          <Link
            to="/projects"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Browse Projects &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
